/**
 * Custom Hook for Header Profile and Verification
 * 
 * Manages user profile data, business verification status, and verification level.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/app/providers/ThemeContext.jsx';
import { authService } from '@/features/auth/services';
import { kycService, profileService } from '@/features/settings/services';
import { tokenService } from '@/features/auth/services/tokenService.js';
import { logger } from '@/shared/utils/logger.js';
import { useQuery } from '@/shared/hooks';
import { queryKeys } from '@/shared/utils/queryKeys.js';

/**
 * Parse verification number from various formats
 */
const parseVerificationNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) { return value; }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) { return parsed; }
  }
  return fallback;
};

/**
 * Resolve verification payload from API response
 */
const resolveVerificationPayload = (response) => {
  if (!response) { return {}; }
  if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
    return response.data;
  }
  if (response.payload && typeof response.payload === 'object') {
    return response.payload;
  }
  return response;
};

/**
 * Map business status string to normalized status
 */
const mapBusinessStatus = (status) => {
  if (!status) { return 'unverified'; }
  const normalized = status.toString().toLowerCase();
  if (normalized.includes('verify')) { return 'verified'; }
  if (normalized.includes('pend')) { return 'pending'; }
  if (normalized.includes('reject')) { return 'rejected'; }
  if (normalized.includes('suspend')) { return 'suspended'; }
  return 'unverified';
};

/**
 * Build business verification object from payload
 */
const buildBusinessVerification = (payload = {}) => {
  const level = parseVerificationNumber(payload.level ?? payload.verificationLevel ?? payload.currentLevel ?? payload.tierLevel);
  const tier = payload.tier ?? payload.name ?? payload.tierName ?? payload.levelName ?? `Tier ${level}`;
  const status = mapBusinessStatus(payload.status ?? payload.verificationStatus ?? payload.state ?? payload.tierStatus);
  const transactionLimit = payload.transactionLimit ?? payload.limit ?? payload.limits ?? payload.maxTransactionAmount ?? null;

  return {
    level,
    tier,
    status,
    transactionLimit,
    ...payload,
  };
};

export const useHeaderProfile = () => {
  const { t } = useTranslation();
  const { effectiveTheme } = useTheme();
  const [userData, setUserData] = useState(null);
  const [businessVerification, setBusinessVerification] = useState(null);
  const [verificationLevel, setVerificationLevel] = useState(null);

  // Load user data from localStorage on mount
  useEffect(() => {
    const loadUserData = async () => {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        try {
          const parsedUserData = JSON.parse(userDataString);
          setUserData(parsedUserData);

          // Load cached business verification status if available
          try {
            const cachedStatus = localStorage.getItem('businessVerificationStatus');
            if (cachedStatus) {
              setBusinessVerification(JSON.parse(cachedStatus));
            }
          } catch (error) {
            logger.error('Error parsing cached verification status', { error });
          }
        } catch (error) {
          logger.error('Error parsing user data', { error });
        }
      }
    };

    loadUserData();
  }, []);

  // Update verification level when business verification data changes
  useEffect(() => {
    if ((userData?.usertype?.toLowerCase() === 'business_contact' || userData?.usertype?.toLowerCase() === 'business_member') && businessVerification) {
      const level = businessVerification.level;
      if (typeof level === 'number') {
        setVerificationLevel(level);
      }
    }
  }, [businessVerification, userData?.usertype]);

  // Get user type and business ID
  const userType = userData?.usertype?.toLowerCase();
  const normalizeBusinessId = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    const lowered = str.toLowerCase();
    if (lowered === 'undefined' || lowered === 'null' || lowered === 'nan') return null;
    return str;
  };

  let businessId = normalizeBusinessId(userData?.business?.id || userData?.businesses?.[0]?.id);
  const normalizedBusinessId = normalizeBusinessId(businessId);

  // For team members, businessId might be in team_members array
  if (!businessId && userType === 'business_member' && userData?.team_members?.[0]?.teams?.business_id) {
    businessId = normalizeBusinessId(userData.team_members[0].teams.business_id);
  }

  // For team members, businessId might not be in userData, so we need to fetch it from profile
  const { data: profileData } = useQuery({
    queryKey: ['userProfile', 'businessId'],
    endpoint: userType === 'business_member' || userType === 'business_contact'
      ? '/api/auth/profile/contact-person/personal-info'
      : '/api/auth/profile/personal-info',
    enabled: !!(userType === 'business_member' && !businessId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    select: (response) => {
      const pdata = response?.data || response;
      return pdata?.business?.id
        || pdata?.businesses?.[0]?.id
        || pdata?.business_id
        || pdata?.businessId
        || pdata?.user?.business?.id;
    },
  });

  // Update businessId if we got it from profile
  if (profileData && !businessId) {
    businessId = normalizeBusinessId(profileData);
  }
  const finalBusinessId = normalizeBusinessId(businessId);

  // Debug logging for team members
  if (userType === 'business_member') {
    logger.debug('Team member verification setup', {
      userType,
      businessId,
      profileData,
      queryEnabled: !!((userType === 'business_contact' || userType === 'business_member') && businessId),
      userData: userData,
      userUsertype: userData?.usertype
    });
  }

  // Additional debugging for all business users
  if (userType === 'business_contact' || userType === 'business_member') {
    logger.debug('Business user verification setup', {
      userType,
      businessId,
      profileData,
      businessVerificationQueryEnabled: !!((userType === 'business_contact' || userType === 'business_member') && businessId),
      individualQueryEnabled: !!(userType && userType === 'individual')
    });
  }

  // Monitor changes in userType and businessId
  useEffect(() => {
    logger.debug('useHeaderProfile - userType and businessId changed', {
      userType,
      businessId,
      profileData,
      isBusinessContact: userType === 'business_contact',
      isBusinessMember: userType === 'business_member',
      isIndividual: userType === 'individual',
      businessVerificationShouldBeEnabled: !!((userType === 'business_contact' || userType === 'business_member') && businessId)
    });
  }, [userType, businessId, profileData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts or async operations
      logger.debug('useHeaderProfile - Cleaning up on unmount');
    };
  }, []);

  // Use React Query to fetch verification level with automatic refetching
  // For business accounts, don't call the individual verification level endpoint
  // Business verification level is handled separately via business-specific endpoints
  const { data: verificationData } = useQuery({
    queryKey: queryKeys.settings.verificationLevel(userType, businessId),
    endpoint: '/auth/get-verification-level',
    enabled: !!(userType && userType === 'individual'), // Only enable for individual users, not business accounts
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false, // Reduced from true to prevent excessive refetches
    select: (resp) => {
      const payload = resolveVerificationPayload(resp);

      // Debug logging
      logger.debug('Individual verification query called', {
        userType,
        enabled: !!(userType && userType === 'individual'),
        userData: userData?.usertype
      });

      // Only handle individual users here
      if (userType === 'individual') {
        const level = parseVerificationNumber(payload?.verificationLevel ?? payload?.level ?? payload);
        tokenService.setVerificationLevel(level);
        return { type: 'individual', data: payload, level };
      }
      return null;
    },
  });

  // Update state when verification data changes
  useEffect(() => {
    if (!verificationData) {
      // Fallback to cached data
      if (userType === 'business_contact' || userType === 'business_member') {
        const cachedStatus = localStorage.getItem('businessVerificationStatus');
        if (cachedStatus) {
          try {
            const parsed = JSON.parse(cachedStatus);
            setBusinessVerification(parsed);
            setVerificationLevel(parseVerificationNumber(parsed?.level ?? 0));
          } catch (e) {
            logger.warn('Failed to parse cached business verification status', { error: e });
          }
        }
      } else if (userType === 'individual') {
        const storedLevel = tokenService.getVerificationLevel();
        const fallbackLevel = parseVerificationNumber(storedLevel);
        setVerificationLevel(fallbackLevel);
      }
      return;
    }

    if (verificationData.type === 'business') {
      setBusinessVerification(verificationData.data);
      setVerificationLevel(verificationData.level);
    } else if (verificationData.type === 'individual') {
      setVerificationLevel(verificationData.level);
    }
  }, [verificationData, userType]);

  // Use React Query to fetch business verification level (replaces useEffect)
  const { data: businessVerificationData } = useQuery({
    queryKey: queryKeys.settings.verificationLevel(userType, businessId),
    endpoint: finalBusinessId ? `/auth/business/${finalBusinessId}/get-verification-level` : '/auth/get-verification-level',
    enabled: !!((userType === 'business_contact' || userType === 'business_member') && finalBusinessId),
    staleTime: 2 * 60 * 1000, // 2 minutes - verification level doesn't change frequently
    refetchOnWindowFocus: false, // Reduced from true to prevent excessive refetches
    select: (response) => {
      // Debug logging
      logger.debug('Business verification query called', {
        userType,
        businessId: finalBusinessId,
        enabled: !!((userType === 'business_contact' || userType === 'business_member') && finalBusinessId),
        endpoint: finalBusinessId ? `/auth/business/${finalBusinessId}/get-verification-level` : '/auth/get-verification-level'
      });

      const payload = resolveVerificationPayload(response);
      const level = parseVerificationNumber(
        payload?.level ??
        payload?.verificationLevel ??
        payload?.currentLevel ??
        payload?.tierLevel
      );
      const name = payload?.name ?? payload?.tierName ?? payload?.levelName;
      const verificationData = buildBusinessVerification(payload);

      // Cache the verification status
      localStorage.setItem('businessVerificationStatus', JSON.stringify(verificationData));

      // Cache tier name separately if available
      if (name) {
        localStorage.setItem('businessVerificationTier', JSON.stringify({
          name,
          level: level ?? null,
          ts: Date.now()
        }));
      }

      if (typeof level === 'number' && Number.isFinite(level)) {
        tokenService.setVerificationLevel(level);
      }

      return verificationData;
    },
    onError: (error) => {
      logger.warn('Failed to fetch business verification level', { error });
      // Try to load from cache if API fails
      try {
        const cachedStatus = localStorage.getItem('businessVerificationStatus');
        if (cachedStatus) {
          const parsed = JSON.parse(cachedStatus);
          setBusinessVerification(parsed);
          const cachedLevel = parseVerificationNumber(parsed?.level);
          if (cachedLevel > 0) {
            setVerificationLevel(cachedLevel);
          }
        }
      } catch (cacheError) {
        logger.warn('Failed to parse cached business verification status', { error: cacheError });
      }
    },
  });

  // Update state when business verification data changes
  useEffect(() => {
    if (businessVerificationData) {
      setBusinessVerification(businessVerificationData);
      const level = parseVerificationNumber(businessVerificationData?.level);
      if (typeof level === 'number' && Number.isFinite(level)) {
        setVerificationLevel(level);
      }
    }
  }, [businessVerificationData]);

  // Get verification level colors based on theme
  const getVerificationLevelColors = useCallback((rawLevel) => {
    const level = Number.isFinite(rawLevel)
      ? Math.max(0, Math.min(Math.round(rawLevel), 3))
      : 0;

    const isDark = effectiveTheme === 'dark';

    const lightPalettes = [
      {
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.18) 0%, rgba(239, 68, 68, 0.12) 45%, rgba(239, 68, 68, 0.08) 100%)',
        text: '#7f1d1d',
        border: 'rgba(239, 68, 68, 0.35)',
        icon: '#b91c1c',
        iconBackground: 'rgba(239, 68, 68, 0.2)',
        shadow: '0 8px 18px rgba(239, 68, 68, 0.12)',
      },
      {
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fde68a 100%)',
        text: '#7c2d12',
        border: 'rgba(251, 191, 36, 0.35)',
        icon: '#d97706',
        iconBackground: 'rgba(251, 191, 36, 0.22)',
        shadow: '0 8px 18px rgba(251, 191, 36, 0.18)',
      },
      {
        background: 'linear-gradient(135deg, #fefce8 0%, #dcfce7 55%, #bbf7d0 100%)',
        text: '#14532d',
        border: 'rgba(34, 197, 94, 0.25)',
        icon: '#0f766e',
        iconBackground: 'rgba(34, 197, 94, 0.18)',
        shadow: '0 8px 18px rgba(34, 197, 94, 0.18)',
      },
      {
        background: 'linear-gradient(135deg, #ecfdf5 0%, #bbf7d0 45%, #86efac 100%)',
        text: '#065f46',
        border: 'rgba(16, 185, 129, 0.25)',
        icon: '#047857',
        iconBackground: 'rgba(16, 185, 129, 0.2)',
        shadow: '0 10px 20px rgba(16, 185, 129, 0.18)',
      },
    ];

    const darkPalettes = [
      {
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.18) 45%, rgba(220, 38, 38, 0.15) 100%)',
        text: '#fca5a5',
        border: 'rgba(239, 68, 68, 0.5)',
        icon: '#f87171',
        iconBackground: 'rgba(239, 68, 68, 0.3)',
        shadow: '0 8px 18px rgba(239, 68, 68, 0.25)',
      },
      {
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.25) 0%, rgba(251, 191, 36, 0.18) 50%, rgba(245, 158, 11, 0.15) 100%)',
        text: '#fcd34d',
        border: 'rgba(251, 191, 36, 0.5)',
        icon: '#fbbf24',
        iconBackground: 'rgba(251, 191, 36, 0.3)',
        shadow: '0 8px 18px rgba(251, 191, 36, 0.25)',
      },
      {
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.25) 0%, rgba(34, 197, 94, 0.18) 55%, rgba(22, 163, 74, 0.15) 100%)',
        text: '#86efac',
        border: 'rgba(34, 197, 94, 0.5)',
        icon: '#4ade80',
        iconBackground: 'rgba(34, 197, 94, 0.3)',
        shadow: '0 8px 18px rgba(34, 197, 94, 0.25)',
      },
      {
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.22) 45%, rgba(5, 150, 105, 0.18) 100%)',
        text: '#6ee7b7',
        border: 'rgba(16, 185, 129, 0.6)',
        icon: '#34d399',
        iconBackground: 'rgba(16, 185, 129, 0.35)',
        shadow: '0 10px 20px rgba(16, 185, 129, 0.3)',
      },
    ];

    return isDark ? darkPalettes[level] || darkPalettes[0] : lightPalettes[level] || lightPalettes[0];
  }, [effectiveTheme]);

  // Get verification status info
  const getVerificationStatus = useCallback(() => {
    const userType = userData?.usertype?.toLowerCase();

    if (userType === 'business_contact' || userType === 'business_member') {
      let data = businessVerification;
      if (!data) {
        try {
          data = JSON.parse(localStorage.getItem('businessVerificationStatus') || 'null');
        } catch (error) {
          logger.warn('Failed to parse cached business verification status', { error });
        }
      }

      const normalized = buildBusinessVerification(data || {});
      const statusKeyMap = {
        verified: 'verified',
        pending: 'pending',
        rejected: 'rejected',
        suspended: 'suspended',
        unverified: 'unverified',
      };
      const statusKey = statusKeyMap[normalized.status] || 'unverified';
      const translatedStatus = {
        key: statusKey,
        label: t(statusKey, { ns: 'settings' }),
      };
      const level = normalized.level ?? parseVerificationNumber(tokenService.getVerificationLevel());
      const tier = normalized.tier ?? t('defaultBusinessTier', { ns: 'settings' });

      const safeLevel = Number.isFinite(level) ? level : 0;
      return {
        display: `verification level ${safeLevel}`,
        status: translatedStatus,
        level: safeLevel,
        tier,
      };
    }

    const level = parseVerificationNumber(verificationLevel ?? tokenService.getVerificationLevel());
    const statusKey = level >= 2 ? 'verified' : level === 1 ? 'pending' : 'unverified';
    const translatedStatus = {
      key: statusKey,
      label: t(statusKey, { ns: 'settings' }),
    };
    const tierLabel = t(`levelBadge${Math.max(0, Math.min(level, 3))}`, { ns: 'settings', defaultValue: `Level ${level}` });

    const safeLevel = Number.isFinite(level) ? level : 0;
    return {
      display: `verification level ${safeLevel}`,
      status: translatedStatus,
      level: safeLevel,
      tierLabel,
    };
  }, [businessVerification, verificationLevel, userData?.usertype, t]);

  const verificationInfo = useMemo(() => getVerificationStatus(), [getVerificationStatus]);

  // Get verification levels based on user type
  const verificationLevels = useMemo(() => {
    // Same limits for business and individual
    return [
      { level: 0, label: t('level0Unverified', { ns: 'settings', defaultValue: 'Level 0: Unverified (Basic)' }), amount: 'GHS 0', description: t('level0Description', { ns: 'settings', defaultValue: 'Email and phone number only' }) },
      { level: 1, label: t('level1EmailPhoneVerified', { ns: 'settings', defaultValue: 'Level 1: Email/Phone Verified' }), amount: 'GHS 1,000', description: t('level1Description', { ns: 'settings', defaultValue: 'Email and SMS verification completed' }) },
      { level: 2, label: t('level2IdVerified', { ns: 'settings', defaultValue: 'Level 2: ID Verified' }), amount: 'GHS 20,000', description: t('level2Description', { ns: 'settings', defaultValue: 'Government-issued ID uploaded and verified' }) },
      { level: 3, label: t('level3FullKyc', { ns: 'settings', defaultValue: 'Level 3: Full KYC' }), amount: 'GHS 100,000', description: t('level3Description', { ns: 'settings', defaultValue: 'ID + Address + Enhanced documentation' }) }
    ];
  }, [t]);

  const currentVerificationLevels = verificationLevels;
  const currentLevel = currentVerificationLevels[verificationLevel] || currentVerificationLevels[0];

  return {
    userData,
    businessVerification,
    verificationLevel,
    verificationInfo,
    verificationLevels: currentVerificationLevels,
    currentLevel,
    getVerificationLevelColors,
  };
};

